import { useState } from "react";
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal } from "lucide-react";
import { fmtDate, Loading, Pill, setQuery, todayYmd, useLoad, useMe, useRoute } from "./lib";
import { OPP_STATUS } from "./pr";

export const TYPES: Record<string, { label: string; color: string }> = {
  meeting: { label: "Meetings", color: "var(--ink-2)" },
  content: { label: "Social content", color: "var(--accent)" },
  opportunity: { label: "PR and speaking", color: "var(--lavender)" },
  task: { label: "Tasks", color: "var(--peach)" },
  release: { label: "Releases", color: "var(--green)" },
  partner: { label: "Partner deliverables", color: "var(--yellow)" },
  campaign: { label: "Campaigns", color: "var(--blue)" },
};
const CONFIRMED = new Set(["accepted", "won", "done"]);
type View = "day" | "week" | "month" | "list";
const VIEWS: View[] = ["day", "week", "month", "list"];
const addDay = (ymd: string, n: number) => new Date(Date.parse(ymd + "T12:00:00Z") + n * 86400000).toISOString().slice(0, 10);
function weekLabel(a: string, b: string) {
  const f = (d: string, o: Intl.DateTimeFormatOptions) => new Date(d + "T12:00:00Z").toLocaleDateString("en-US", { ...o, timeZone: "UTC" });
  return a.slice(0, 7) === b.slice(0, 7) ? `${f(a, { month: "short", day: "numeric" })} – ${f(b, { day: "numeric" })}, ${a.slice(0, 4)}` : `${f(a, { month: "short", day: "numeric" })} – ${f(b, { month: "short", day: "numeric", year: "numeric" })}`;
}

export function Calendar() {
  const { me } = useMe();
  const route = useRoute();
  const today = todayYmd();
  // One anchor date drives every view: the month it's in, the week it's in, or the day itself.
  const [anchor, setAnchor] = useState(route.q.get("date") || (route.q.get("month") ? `${route.q.get("month")}-01` : today));
  const [view, setViewState] = useState<View>(() => {
    try {
      const v = localStorage.getItem("momeaze.calendar.view") as View | null;
      if (v && VIEWS.includes(v)) return v;
    } catch {}
    return window.innerWidth < 700 ? "day" : "week";
  });
  const setView = (v: View) => {
    setViewState(v);
    try { localStorage.setItem("momeaze.calendar.view", v); } catch {}
  };
  const month = anchor.slice(0, 7);
  const [y, m] = month.split("-").map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1, 1));
  const start = new Date(first);
  start.setUTCDate(1 - first.getUTCDay());
  const days = Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * 86400000).toISOString().slice(0, 10));
  const weekStart = addDay(anchor, -new Date(anchor + "T12:00:00Z").getUTCDay());
  const weekDays = Array.from({ length: 7 }, (_, i) => addDay(weekStart, i));
  const shown = view === "week" ? weekDays : view === "day" ? [anchor] : days;
  const { data, error } = useLoad<any>(`/api/calendar?from=${[shown[0]!, days[0]!].sort()[0]}&to=${[shown[shown.length - 1]!, days[41]!].sort().reverse()[0]}`, [anchor, view]);
  const types = (route.q.get("types") || Object.keys(TYPES).join(",")).split(",").filter(Boolean);
  const person = route.q.get("person") || "";
  const team = route.q.get("team") || "";
  const onlyMine = route.q.get("needs") === "me";
  const [showFilters, setShowFilters] = useState(false);
  // Searching looks a year back and a year ahead, not just this month.
  const [q, setQ] = useState(route.q.get("q") || "");
  const term = q.trim().toLowerCase();
  const shiftDay = (n: number) => new Date(Date.parse(today + "T12:00:00Z") + n * 86400000).toISOString().slice(0, 10);
  const wide = useLoad<any>(term.length >= 2 ? `/api/calendar?from=${shiftDay(-365)}&to=${shiftDay(365)}` : null);

  const events = (data?.events || []).filter(
    (e: any) => types.includes(e.type) && (!person || e.owner_id === person) && (!team || e.department === team) && (!onlyMine || e.needs_me),
  );
  const matches = term.length >= 2
    ? (wide.data?.events || [])
        .filter((e: any) => types.includes(e.type) && (!person || e.owner_id === person) && (!team || e.department === team) && (!onlyMine || e.needs_me))
        .filter((e: any) => `${e.title} ${e.owner_name ?? ""} ${e.status ?? ""} ${e.waiting_on ?? ""}`.toLowerCase().includes(term))
        .sort((a: any, b: any) => (a.date >= today) === (b.date >= today) ? (a.date >= today ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)) : a.date >= today ? -1 : 1)
    : null;
  const people = [...new Map((data?.events || []).filter((e: any) => e.owner_id).map((e: any) => [e.owner_id, e.owner_name])).entries()] as [string, string][];
  const teams = [...new Set((data?.events || []).map((e: any) => e.department).filter(Boolean))] as string[];
  const shift = (n: number) =>
    setAnchor(view === "week" ? addDay(anchor, 7 * n) : view === "day" ? addDay(anchor, n) : new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10));
  const heading =
    view === "day" ? new Date(anchor + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })
    : view === "week" ? weekLabel(weekDays[0]!, weekDays[6]!)
    : first.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  const on = (d: string) => events.filter((e: any) => e.date === d || (e.end && e.date <= d && e.end >= d));
  const toggle = (t: string) => setQuery({ types: (types.includes(t) ? types.filter((x) => x !== t) : [...types, t]).join(",") || "none" });

  return (
    <div className="page">
      <div className="row between">
        <div className="row">
          <strong style={{ fontSize: 18, minWidth: 190 }}>{heading}</strong>
          <button className="btn sm icon-btn" onClick={() => shift(-1)} aria-label={`Previous ${view}`}><ChevronLeft size={17} aria-hidden /></button>
          <button className="btn sm" onClick={() => setAnchor(today)}>Today</button>
          <button className="btn sm icon-btn" onClick={() => shift(1)} aria-label={`Next ${view}`}><ChevronRight size={17} aria-hidden /></button>
        </div>
        <div className="tabs">
          {VIEWS.map((v) => <button key={v} className={view === v ? "on" : ""} onClick={() => setView(v)}>{v[0]!.toUpperCase() + v.slice(1)}</button>)}
        </div>
      </div>

      <div className="cal-toolbar">
        <label className="cal-search">
          <Search size={16} strokeWidth={2} aria-hidden />
          <input
            type="search"
            placeholder="Search the calendar"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setQ("")}
            aria-label="Search the calendar"
          />
        </label>
        <select className="input cal-select" value={person} onChange={(e) => setQuery({ person: e.target.value || null })} aria-label="Person">
          <option value="">Everyone</option>
          {people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <button className={`chip-toggle ${onlyMine ? "on" : ""}`} aria-pressed={onlyMine} onClick={() => setQuery({ needs: onlyMine ? null : "me" })}>Needs me</button>
        <div style={{ position: "relative" }}>
          <button className={`chip-toggle ${types.length < Object.keys(TYPES).length || team ? "on" : ""}`} aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)} onKeyDown={(e) => e.key === "Escape" && setShowFilters(false)}>
            <SlidersHorizontal size={15} strokeWidth={2} aria-hidden />
            {types.length === Object.keys(TYPES).length && !team ? "Show: everything" : `Show: ${types.length} of ${Object.keys(TYPES).length}${team ? ` · ${team}` : ""}`}
          </button>
          {showFilters && (
            <>
              <div className="popover-scrim" onClick={() => setShowFilters(false)} />
              <div className="popover" onKeyDown={(e) => e.key === "Escape" && setShowFilters(false)}>
                <div className="tiny muted" style={{ fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase" }}>Show on the calendar</div>
                {Object.entries(TYPES).map(([k, t]) => (
                  <label key={k} className="check"><input type="checkbox" checked={types.includes(k)} onChange={() => toggle(k)} /><span className="dot" style={{ background: t.color }} />{t.label}</label>
                ))}
                <div className="row between" style={{ marginTop: 4 }}>
                  <button className="btn ghost sm" onClick={() => setQuery({ types: null })}>Show all</button>
                  <select className="input cal-select" value={team} onChange={(e) => setQuery({ team: e.target.value || null })} aria-label="Team">
                    <option value="">All teams</option>
                    {teams.map((t) => <option key={t} value={t}>{t === "pr" ? "PR" : t[0]!.toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="tiny" style={{ borderTop: "1px solid var(--line)", paddingTop: 8 }}><GoogleStatus c={data?.calendars} /></div>
              </div>
            </>
          )}
        </div>
      </div>

      {matches ? (
        <section className="card stack" style={{ padding: "8px 16px" }}>
          <div className="row between" style={{ paddingTop: 8 }}>
            <strong className="small">{wide.data ? `${matches.length} match${matches.length === 1 ? "" : "es"} for "${q.trim()}"` : "Searching…"}</strong>
            <span className="muted tiny">A year back and a year ahead · upcoming first</span>
          </div>
          {matches.length ? (
            <div className="list">
              {matches.map((e: any) => (
                <a key={e.id} href={e.href} className="item clickable" style={{ color: "inherit", textDecoration: "none", opacity: e.date < today ? 0.7 : 1 }}>
                  <span className="dot" style={{ background: TYPES[e.type]?.color }} />
                  <span className="small muted" style={{ width: 90 }}>{fmtDate(e.date)}{e.time ? ` ${e.time}` : ""}</span>
                  <span className="grow">
                    <span className="title">{e.title}</span>
                    <span className="small muted" style={{ display: "block" }}>{statusLine(e, me.id)}</span>
                  </span>
                  {e.needs_me && <Pill tone="warn">Needs you</Pill>}
                </a>
              ))}
            </div>
          ) : wide.data ? <div className="empty" style={{ margin: 12 }}>Nothing on the calendar matches. Try ⌘K to search the whole portal.</div> : <Loading />}
        </section>
      ) : !data ? (
        <Loading error={error} />
      ) : view === "week" || view === "day" ? (
        <TimeGrid days={shown} events={events} today={today} onPickDay={(d) => (setAnchor(d), setView("day"))} />
      ) : view === "month" ? (
        <div className="cal">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="dow">{d}</div>)}
          {days.map((d) => (
            <div key={d} className={`day ${d.slice(0, 7) !== month ? "other" : ""} ${d === today ? "today" : ""}`}>
              <button className="num" onClick={() => (setAnchor(d), setView("day"))} aria-label={`Open ${d}`}>{Number(d.slice(8))}</button>
              {on(d).slice(0, 5).map((e: any) => <Ev key={e.id + d} e={e} />)}
              {on(d).length > 5 && <span className="tiny muted">+{on(d).length - 5} more</span>}
            </div>
          ))}
        </div>
      ) : (
        <section className="card" style={{ padding: "8px 16px" }}>
          {events.filter((e: any) => e.date >= days[0]! && e.date.slice(0, 7) <= month).length ? (
            <div className="list">
              {events.filter((e: any) => e.date.slice(0, 7) === month || (e.end && e.end.slice(0, 7) >= month && e.date.slice(0, 7) <= month)).map((e: any) => (
                <a key={e.id} href={e.href} className="item clickable" style={{ color: "inherit", textDecoration: "none" }}>
                  <span className="dot" style={{ background: TYPES[e.type]?.color }} />
                  <span className="small muted" style={{ width: 70 }}>{fmtDate(e.date)}{e.time ? ` ${e.time}` : ""}</span>
                  <span className="grow">
                    <span className="title">{e.title}</span>
                    <span className="small muted" style={{ display: "block" }}>{statusLine(e, me.id)}</span>
                  </span>
                  {e.needs_me && <Pill tone="warn">Needs you</Pill>}
                </a>
              ))}
            </div>
          ) : <div className="empty" style={{ margin: 12 }}>Nothing this month with these filters.</div>}
        </section>
      )}
    </div>
  );
}

function statusLine(e: any, meId: string) {
  const s = e.type === "opportunity" ? OPP_STATUS[e.status]?.label ?? e.status : e.status.replace(/_/g, " ");
  const tentative = e.type === "opportunity" && !e.title.startsWith("Due:") && !e.title.startsWith("Follow up:") && !CONFIRMED.has(e.status) ? " · not confirmed" : "";
  const who = e.needs_me ? "needs you" : e.waiting_on ? `waiting on ${e.waiting_on}` : e.owner_id === meId ? "yours" : e.owner_name ? `with ${e.owner_name}` : "";
  return [s + tentative, who].filter(Boolean).join(" · ");
}

function Ev({ e }: { e: any }) {
  const { me } = useMe();
  const tentative = e.type === "opportunity" && !e.title.startsWith("Due:") && !e.title.startsWith("Follow up:") && !CONFIRMED.has(e.status);
  return (
    <a
      href={e.href}
      target={e.href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className={`ev ${e.type === "meeting" ? "meeting" : ""}`}
      style={{ borderLeftColor: TYPES[e.type]?.color, color: "inherit", textDecoration: "none", display: "block", fontStyle: tentative ? "italic" : undefined, fontWeight: e.needs_me ? 600 : undefined }}
      title={`${e.title} · ${statusLine(e, me.id)}`}
    >
      {e.time ? `${e.time} ` : ""}{e.title}{tentative ? " ?" : ""}
    </a>
  );
}

function GoogleStatus({ c }: { c: any }) {
  if (!c) return null;
  if (c.google === "connected") return <span className="muted tiny">Your Google Calendar is connected{c.email ? ` (${c.email})` : ""}. Teammates' meetings show as Busy.</span>;
  if (c.google === "error") return <span className="tiny" style={{ color: "var(--warn)" }}>{c.error} <a href="#/settings">Settings</a></span>;
  if (c.google === "not_connected") return <span className="muted tiny">Your meetings aren't here yet. <a href="/api/calendar/google/connect">Connect Google Calendar</a></span>;
  return <span className="muted tiny">Google Calendar isn't set up on the portal yet.</span>;
}

const HOUR_START = 6, HOUR_END = 22, HOUR_PX = 52;
const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const clock = (t: string) => {
  const h = Number(t.slice(0, 2)), m = t.slice(3, 5);
  return `${h % 12 || 12}${m === "00" ? "" : ":" + m}${h < 12 ? "am" : "pm"}`;
};

// Day and week: an hour grid for anything with a time, and an "all day" strip for deadlines and dated items.
function TimeGrid({ days, events, today, onPickDay }: { days: string[]; events: any[]; today: string; onPickDay: (d: string) => void }) {
  const { me } = useMe();
  const onDay = (d: string) => events.filter((e) => e.date === d || (e.end && e.date <= d && e.end >= d));
  const timed = (d: string) => onDay(d).filter((e) => e.time && e.date === d);
  const allDay = (d: string) => onDay(d).filter((e) => !e.time || e.date !== d);
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
  const now = new Intl.DateTimeFormat("en-GB", { timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());
  const cols = `56px repeat(${days.length}, minmax(0, 1fr))`;

  // Overlapping items share the column side by side.
  const lay = (list: any[]) => {
    const items = list
      .map((e) => {
        const s = Math.max(mins(e.time), HOUR_START * 60);
        const end = e.end_time ? Math.max(mins(e.end_time), s + 15) : s + 30;
        return { e, s, end, lane: 0, lanes: 1 };
      })
      .sort((a, b) => a.s - b.s || b.end - a.end);
    const groups: (typeof items)[] = [];
    for (const it of items) {
      const g = groups[groups.length - 1];
      if (g && it.s < Math.max(...g.map((x) => x.end))) g.push(it);
      else groups.push([it]);
    }
    for (const g of groups) {
      const laneEnds: number[] = [];
      for (const it of g) {
        let l = laneEnds.findIndex((end) => end <= it.s);
        if (l < 0) l = laneEnds.length;
        laneEnds[l] = it.end;
        it.lane = l;
      }
      for (const it of g) it.lanes = laneEnds.length;
    }
    return items;
  };

  return (
    <div className="tgrid card" style={{ padding: 0 }}>
      <div className="tg-head" style={{ gridTemplateColumns: cols }}>
        <div />
        {days.map((d) => (
          <button key={d} className={`tg-day ${d === today ? "today" : ""}`} onClick={() => onPickDay(d)}>
            <span>{new Date(d + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}</span>
            <b>{Number(d.slice(8))}</b>
          </button>
        ))}
      </div>
      <div className="tg-allday" style={{ gridTemplateColumns: cols }}>
        <div className="tg-label">All day</div>
        {days.map((d) => (
          <div key={d} className="tg-allday-cell">{allDay(d).map((e) => <Ev key={e.id + d} e={e} />)}</div>
        ))}
      </div>
      <div className="tg-body" style={{ gridTemplateColumns: cols }}>
        <div>{hours.map((h) => <div key={h} className="tg-hour-label" style={{ height: HOUR_PX }}>{clock(`${String(h).padStart(2, "0")}:00`)}</div>)}</div>
        {days.map((d) => (
          <div key={d} className={`tg-col ${d === today ? "today" : ""}`} style={{ height: hours.length * HOUR_PX }}>
            {hours.map((h) => <div key={h} className="tg-line" style={{ top: (h - HOUR_START) * HOUR_PX }} />)}
            {d === today && mins(now) >= HOUR_START * 60 && mins(now) < HOUR_END * 60 && <div className="tg-now" style={{ top: ((mins(now) - HOUR_START * 60) / 60) * HOUR_PX }} />}
            {lay(timed(d)).map(({ e, s, end, lane, lanes }) => (
              <a
                key={e.id}
                href={e.href}
                target={e.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className={`tg-ev ${e.type}`}
                style={{
                  top: ((s - HOUR_START * 60) / 60) * HOUR_PX + 1,
                  height: Math.max(20, ((Math.min(end, HOUR_END * 60) - s) / 60) * HOUR_PX - 3),
                  left: `calc(${(lane / lanes) * 100}% + 2px)`,
                  width: `calc(${100 / lanes}% - 4px)`,
                  borderLeftColor: TYPES[e.type]?.color,
                  fontWeight: e.needs_me ? 600 : undefined,
                }}
                title={`${clock(e.time)}${e.end_time ? `–${clock(e.end_time)}` : ""} ${e.title} · ${statusLine(e, me.id)}`}
              >
                <span className="tg-ev-time">{clock(e.time)}{e.end_time ? `–${clock(e.end_time)}` : ""}</span>
                <span className="tg-ev-title">{e.title}</span>
              </a>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
